import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import type { TFunction } from 'i18next';
import { getApiErrorMessage } from 'utils/apiError';
import {
  type DiagnosisSelectionChoice,
  type ExportStatus,
  ResultTemplateKey,
  type VitalParameters,
} from 'types/result';
import { useDataExport } from 'hooks/useDataExport';
import type { VitalParameterKey } from 'widgets/results/VitalParametersSection';

export interface UseResultExportProps {
  resultId: string | number;
  anamnesisId: string | undefined;
  vitalParameterSelectionMap: Record<VitalParameterKey, boolean>;
  exportStatus: ExportStatus | undefined;
  saveEditsBeforeExport: (extraFields?: Record<string, unknown>) => Promise<void>;
  pendingDiagnosisSelection: () => Record<string, DiagnosisSelectionChoice> | null;
  pendingEbmSelection: () => Record<string, boolean> | null;
  pendingEbmDuration: () => number | null;
  pendingVitalParameters: () => VitalParameters | null;
  clearPendingVitalParameters: () => void;
  hasOpenImmunization?: boolean;
  /** Resolves false when an immunization edit still in flight has failed. */
  flushImmunizationEdits?: () => Promise<boolean>;
  t: TFunction;
}

export interface UseResultExportReturn {
  isExporting: boolean;
  handleExport: () => Promise<boolean>;
  handleRetryExport: () => Promise<boolean>;
}

export function useResultExport({
  resultId,
  anamnesisId,
  vitalParameterSelectionMap,
  exportStatus,
  saveEditsBeforeExport,
  pendingDiagnosisSelection,
  pendingEbmSelection,
  pendingEbmDuration,
  pendingVitalParameters,
  clearPendingVitalParameters,
  hasOpenImmunization = false,
  flushImmunizationEdits,
  t,
}: UseResultExportProps): UseResultExportReturn {
  const [isExporting, setIsExporting] = useState<boolean>(false);
  const [checkedSections, setCheckedSections] = useState<Set<ResultTemplateKey | string>>(
    new Set([
      ResultTemplateKey.Anamnesis,
      ResultTemplateKey.Findings,
      ResultTemplateKey.Diagnosis,
      ResultTemplateKey.Therapy,
      ResultTemplateKey.Procedure,
      ResultTemplateKey.Comment,
      ResultTemplateKey.EbmCodes,
    ])
  );

  // An untransferred immunization always travels with the export; it cannot be unchecked.
  const sectionsToExport = useMemo(
    () =>
      hasOpenImmunization
        ? new Set([...checkedSections, ResultTemplateKey.Immunization])
        : checkedSections,
    [checkedSections, hasOpenImmunization]
  );

  const retryFilter = useMemo(() => {
    if (!exportStatus?.result) return undefined;

    const CHECKED_TO_API: Partial<Record<string, string>> = {
      [ResultTemplateKey.Anamnesis]: ResultTemplateKey.Anamnesis,
      [ResultTemplateKey.Findings]: ResultTemplateKey.Findings,
      [ResultTemplateKey.Therapy]: ResultTemplateKey.Therapy,
      [ResultTemplateKey.Procedure]: ResultTemplateKey.Procedure,
      [ResultTemplateKey.Comment]: ResultTemplateKey.Comment,
      [ResultTemplateKey.Diagnosis]: ResultTemplateKey.DiagnosedIcd,
      [ResultTemplateKey.EbmCodes]: ResultTemplateKey.EbmCodes,
      [ResultTemplateKey.Immunization]: ResultTemplateKey.Immunization,
    };

    const sectionFullySucceeded = new Map<string, boolean>();
    exportStatus.result.forEach((entry) => {
      const type = entry.type as string;
      const prev = sectionFullySucceeded.get(type);
      const succeeded = entry.successfulTransmitted === true;
      sectionFullySucceeded.set(type, prev === undefined ? succeeded : prev && succeeded);
    });

    const currentlySelectedApiKeys = new Set<string>();
    sectionsToExport.forEach((key) => {
      const apiKey = CHECKED_TO_API[key];
      if (apiKey) currentlySelectedApiKeys.add(apiKey);
    });
    const hasSelectedVitals = Object.values(vitalParameterSelectionMap).some(Boolean);
    if (hasSelectedVitals) {
      currentlySelectedApiKeys.add(ResultTemplateKey.VitalParameters);
    }

    const toRetry = new Set<string>();
    currentlySelectedApiKeys.forEach((apiKey) => {
      if (!sectionFullySucceeded.has(apiKey) || sectionFullySucceeded.get(apiKey) === false) {
        toRetry.add(apiKey);
      }
    });

    return toRetry.size > 0 ? toRetry : undefined;
  }, [exportStatus?.result, sectionsToExport, vitalParameterSelectionMap]);

  const { handleDataExportAsync, successfullyExportedFields } = useDataExport({
    resultId,
    anamnesisId,
    checkedSections: sectionsToExport,
    vitalParameterSelectionMap,
  });

  const autoUncheckedFields = useRef<Set<string>>(new Set());
  useEffect(() => {
    autoUncheckedFields.current = new Set();
  }, [resultId]);
  useEffect(() => {
    if (!successfullyExportedFields.size) return;
    const newlySuccessful: string[] = [];
    successfullyExportedFields.forEach((field) => {
      if (!autoUncheckedFields.current.has(field)) {
        newlySuccessful.push(field);
        autoUncheckedFields.current.add(field);
      }
    });
    if (newlySuccessful.length === 0) return;
    setCheckedSections((prev) => {
      let changed = false;
      const next = new Set(prev);
      newlySuccessful.forEach((field) => {
        const sectionKey =
          (field as ResultTemplateKey) === ResultTemplateKey.DiagnosedIcd
            ? ResultTemplateKey.Diagnosis
            : field;
        if (next.delete(sectionKey)) changed = true;
      });
      return changed ? next : prev;
    });
  }, [successfullyExportedFields]);

  const handleDataExportAsyncWithEdits = useCallback(
    async (retryFilter?: Set<string>): Promise<boolean> => {
      if (isExporting) return false;
      try {
        setIsExporting(true);
        const diagnosisSelection = pendingDiagnosisSelection();
        const ebmProposalSelection = pendingEbmSelection();
        const ebmDurationMinutes = pendingEbmDuration();
        const vitalParameters = pendingVitalParameters();
        await saveEditsBeforeExport({
          ...(diagnosisSelection ? { diagnosisSelection } : {}),
          ...(ebmProposalSelection ? { ebmProposalSelection } : {}),
          ...(ebmDurationMinutes !== null ? { durationSeconds: ebmDurationMinutes * 60 } : {}),
          ...(vitalParameters ? { vitalParameters } : {}),
        });
        if (vitalParameters) clearPendingVitalParameters();
        if (flushImmunizationEdits && !(await flushImmunizationEdits())) {
          toast.error(t('result.exportError.exportFailed'));
          return false;
        }
        const response = await handleDataExportAsync(retryFilter ? { retryFilter } : undefined);
        if (response.success) {
          toast.success(t('result.exportSuccess'));
          return true;
        }
        toast.error(t('result.exportError.exportFailed'));
        return false;
      } catch (error) {
        toast.error(getApiErrorMessage(error, t, 'result.exportError.exportFailed'));
        return false;
      } finally {
        setIsExporting(false);
      }
    },
    [
      handleDataExportAsync,
      isExporting,
      t,
      saveEditsBeforeExport,
      pendingDiagnosisSelection,
      pendingEbmSelection,
      pendingEbmDuration,
      pendingVitalParameters,
      clearPendingVitalParameters,
      flushImmunizationEdits,
    ]
  );

  const handleExport = useCallback(
    () => handleDataExportAsyncWithEdits(),
    [handleDataExportAsyncWithEdits]
  );
  const handleRetryExport = useCallback(
    () => handleDataExportAsyncWithEdits(retryFilter),
    [handleDataExportAsyncWithEdits, retryFilter]
  );

  return {
    isExporting,
    handleExport,
    handleRetryExport,
  };
}
